'use-client';

import { arrow } from '@/assets';
import { usePage } from '@/hooks/use-page';
import Image from 'next/image';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PaginationItem from './PaginationItem';
import Input from '@/ui/Input';
import { createPortal } from 'react-dom';
import {
    buildPresetsDropdownPosition,
    estimatePresetsDropdownHeight,
    PresetsDropdownPosition,
} from '@/components/pagination/presets-dropdown-position';
import { overlayLayers } from '@/ui/overlay-layers';
import { useOverlayPortalContext } from '@/ui/overlay-portal-context';

type PaginationProps = {
    setPage: (val: number) => void;
    numOfPages: number;
    elementsOnPage: number;
    onElementsOnPageUpdate: (count: number) => void;
    totalRows: number;
    presetValues?: number[];
    currentPage?: number;
};

const DESKTOP_DISPLAYED_COUNT = 5;
const COMPACT_DISPLAYED_COUNT = 2;
const SMALLEST_PAGE = 1;
const MIN_ROWS_ON_PAGE = 1;
const DEFAULT_PRESET_VALUES = [20, 100, 500, 1000];

const normalizePresetValues = (values: number[]) =>
    Array.from(
        new Set(
            values
                .map((value) => Math.trunc(value))
                .filter((value) => Number.isFinite(value) && value >= MIN_ROWS_ON_PAGE),
        ),
    );

const Pagination: FC<PaginationProps> = ({
    setPage,
    numOfPages,
    elementsOnPage,
    onElementsOnPageUpdate,
    totalRows,
    presetValues,
    currentPage,
}) => {
    const { portalTarget: overlayPortalTarget, isInsideModal } = useOverlayPortalContext();
    const [pagesCountInput, setPagesCountInput] = useState(elementsOnPage.toString());
    const [displayedItemsCount, setDisplayedItemsCount] = useState(DESKTOP_DISPLAYED_COUNT);
    const [isPresetsDropdownOpen, setIsPresetsDropdownOpen] = useState(false);
    const [presetsDropdownPosition, setPresetsDropdownPosition] = useState<PresetsDropdownPosition | null>(null);
    const skipDebouncedUpdateRef = useRef(false);
    const presetsWrapperRef = useRef<HTMLDivElement | null>(null);
    const presetsDropdownRef = useRef<HTMLDivElement | null>(null);

    const routePage = usePage();
    const page = currentPage ?? routePage;
    const normalizedNumOfPages = Math.max(SMALLEST_PAGE, numOfPages);
    const itemsTemplate = useMemo(() => new Array(displayedItemsCount).fill(0), [displayedItemsCount]);
    const normalizedPresetValues = useMemo(() => {
        const calculatedPresetValue = presetValues?.[0];
        const sourceValues =
            typeof calculatedPresetValue === 'number'
                ? [calculatedPresetValue, ...DEFAULT_PRESET_VALUES]
                : DEFAULT_PRESET_VALUES;
        const values = normalizePresetValues(sourceValues);

        return values.length ? values : DEFAULT_PRESET_VALUES;
    }, [presetValues]);

    const items = useMemo(() => {
        if (normalizedNumOfPages <= displayedItemsCount + 1) {
            return itemsTemplate.slice(0, normalizedNumOfPages - 2).map((_, index) => index + 2);
        }
        if (page <= displayedItemsCount) {
            return itemsTemplate.map((_, index) => index + 2);
        } else if (normalizedNumOfPages - page < displayedItemsCount) {
            return itemsTemplate.map((_, index) => normalizedNumOfPages - displayedItemsCount + index);
        }
        const start = page - Math.floor(displayedItemsCount / 2);
        return itemsTemplate.map((_, index) => start + index);
    }, [displayedItemsCount, itemsTemplate, page, normalizedNumOfPages]);

    const getFallbackDropdownHeight = useCallback(
        () => estimatePresetsDropdownHeight(normalizedPresetValues.length),
        [normalizedPresetValues.length],
    );

    const getDropdownHeight = useCallback(() => {
        const measuredDropdownHeight = presetsDropdownRef.current?.offsetHeight ?? 0;
        if (measuredDropdownHeight > 0) {
            return measuredDropdownHeight;
        }

        return getFallbackDropdownHeight();
    }, [getFallbackDropdownHeight]);

    const updatePresetsDropdownPosition = useCallback(
        (dropdownHeight: number) => {
            if (typeof window === 'undefined') return;

            const wrapper = presetsWrapperRef.current;
            if (!wrapper) return;

            const wrapperRect = wrapper.getBoundingClientRect();
            setPresetsDropdownPosition(
                buildPresetsDropdownPosition({
                    triggerRect: wrapperRect,
                    dropdownHeight,
                    viewportHeight: window.innerHeight,
                }),
            );
        },
        [setPresetsDropdownPosition],
    );

    const setCurrentPage = (nextPage: number) => {
        if (nextPage > normalizedNumOfPages) {
            setPage(SMALLEST_PAGE);
            return;
        }
        if (nextPage < SMALLEST_PAGE) {
            setPage(SMALLEST_PAGE);
            return;
        }

        setPage(nextPage);
    };

    const setNextPage = () => {
        if (page >= normalizedNumOfPages) return;

        setPage(page + 1);
    };

    const setPrevPage = () => {
        if (page <= 1) return;

        setPage(page - 1);
    };

    const onInputChange = (value: string) => {
        if (!value) {
            setPagesCountInput('');
            return;
        }

        if (!/^\d+$/.test(value)) return;

        const normalizedValue = value.replace(/^0+/, '');
        if (!normalizedValue) {
            setPagesCountInput('');
            return;
        }

        const num = Number(normalizedValue);
        if (!isFinite(num) || isNaN(num)) return;
        if (num < MIN_ROWS_ON_PAGE) return;

        setPagesCountInput(String(num));
    };

    useEffect(() => {
        if (skipDebouncedUpdateRef.current) {
            skipDebouncedUpdateRef.current = false;
            return;
        }

        if (!pagesCountInput) return;

        const nextCount = Number(pagesCountInput);
        if (!Number.isInteger(nextCount) || nextCount < MIN_ROWS_ON_PAGE) return;
        if (nextCount === elementsOnPage) return;

        const timeout = setTimeout(() => onElementsOnPageUpdate(nextCount), 500);

        return () => clearTimeout(timeout);
    }, [pagesCountInput, elementsOnPage, onElementsOnPageUpdate]);

    useEffect(() => {
        setPagesCountInput(elementsOnPage.toString());
    }, [elementsOnPage]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQuery = window.matchMedia('(min-width: 1024px)');
        const setDisplayCount = (isDesktop: boolean) =>
            setDisplayedItemsCount(isDesktop ? DESKTOP_DISPLAYED_COUNT : COMPACT_DISPLAYED_COUNT);

        setDisplayCount(mediaQuery.matches);

        const onMediaChange = (event: MediaQueryListEvent) => {
            setDisplayCount(event.matches);
        };

        mediaQuery.addEventListener('change', onMediaChange);
        return () => mediaQuery.removeEventListener('change', onMediaChange);
    }, []);

    useEffect(() => {
        if (page > normalizedNumOfPages) {
            setPage(SMALLEST_PAGE);
        }
    }, [page, normalizedNumOfPages, setPage]);

    useEffect(() => {
        if (!isPresetsDropdownOpen) return;

        const updatePosition = () => updatePresetsDropdownPosition(getDropdownHeight());

        updatePosition();

        const onWindowResize = () => updatePosition();
        const animationFrameId = window.requestAnimationFrame(updatePosition);

        window.addEventListener('resize', onWindowResize);
        window.addEventListener('scroll', onWindowResize, true);

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', onWindowResize);
            window.removeEventListener('scroll', onWindowResize, true);
        };
    }, [getDropdownHeight, isPresetsDropdownOpen, updatePresetsDropdownPosition]);

    useEffect(() => {
        if (!isPresetsDropdownOpen) return;

        const onMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (presetsWrapperRef.current?.contains(target)) return;
            if (presetsDropdownRef.current?.contains(target)) return;

            setIsPresetsDropdownOpen(false);
        };

        window.addEventListener('mousedown', onMouseDown);
        return () => window.removeEventListener('mousedown', onMouseDown);
    }, [isPresetsDropdownOpen]);

    const onPresetValueClick = (value: number) => {
        skipDebouncedUpdateRef.current = true;
        setPagesCountInput(value.toString());
        setIsPresetsDropdownOpen(false);

        if (value !== elementsOnPage) {
            onElementsOnPageUpdate(value);
        }
    };
    const resolvedPortalTarget = overlayPortalTarget ?? (typeof document !== 'undefined' ? document.body : null);
    const dropdownLayer = isInsideModal ? overlayLayers.overlayModalPopover : overlayLayers.overlayPopover;

    return (
        <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-nowrap items-center gap-1">
                <PaginationItem
                    key={'left'}
                    data={<Image src={arrow} alt="arrow" width={14} height={14} className={'rotate-180 max-w-none'} />}
                    isActive={false}
                    onClick={setPrevPage}
                />
                <PaginationItem
                    key={SMALLEST_PAGE}
                    data={SMALLEST_PAGE}
                    isActive={page === SMALLEST_PAGE}
                    onClick={() => setCurrentPage(SMALLEST_PAGE)}
                />
                {page >= displayedItemsCount && normalizedNumOfPages > displayedItemsCount + 1 && (
                    <PaginationItem
                        key={'left-manydots'}
                        data={'...'}
                        isActive={false}
                        onClick={() => setCurrentPage(page - displayedItemsCount)}
                    />
                )}
                {normalizedNumOfPages > 2 &&
                    items.map((elem) => (
                        <PaginationItem
                            key={elem}
                            data={elem}
                            isActive={elem === page}
                            onClick={() => setCurrentPage(elem)}
                        />
                    ))}
                {normalizedNumOfPages - page >= displayedItemsCount - 1 &&
                    normalizedNumOfPages > displayedItemsCount + 1 && (
                        <PaginationItem
                            key={'right-manydots'}
                            data={'...'}
                            isActive={false}
                            onClick={() => setCurrentPage(page + displayedItemsCount)}
                        />
                    )}
                {normalizedNumOfPages > 1 && (
                    <PaginationItem
                        key={normalizedNumOfPages}
                        data={normalizedNumOfPages}
                        isActive={page === normalizedNumOfPages}
                        onClick={() => setCurrentPage(normalizedNumOfPages)}
                    />
                )}
                <PaginationItem
                    key={'right'}
                    data={<Image src={arrow} alt="arrow" width={14} height={14} className="max-w-none" />}
                    isActive={false}
                    onClick={setNextPage}
                />
            </div>
            <div className="flex shrink-0 items-center gap-1">
                <span className="whitespace-nowrap text-[11px] leading-4 text-slate-500 lg:hidden">на стр.</span>
                <div ref={presetsWrapperRef} className="relative w-[80px] md:w-[90px] lg:w-[84px]">
                    <Input
                        aria-label="Значение строк на странице"
                        value={pagesCountInput}
                        onChange={(e) => onInputChange(e.target.value)}
                        uiSize="compact"
                        className="!min-h-[30px] px-1.5 pr-5 text-center"
                    />
                    <button
                        type="button"
                        aria-label="Открыть список значений на страницу"
                        className="absolute right-0 top-0 flex h-full w-5 items-center justify-center text-slate-500"
                        onClick={() =>
                            setIsPresetsDropdownOpen((currentValue) => {
                                const nextValue = !currentValue;
                                if (nextValue) {
                                    updatePresetsDropdownPosition(getDropdownHeight());
                                }
                                return nextValue;
                            })
                        }
                    >
                        <Image
                            src={arrow}
                            alt="toggle-rows-per-page-presets"
                            width={12}
                            height={12}
                            className={isPresetsDropdownOpen ? 'rotate-270' : 'rotate-90'}
                        />
                    </button>
                </div>
                <span className="whitespace-nowrap text-[11px] leading-4 text-slate-500">
                    из {Math.max(totalRows, 0)}
                </span>
            </div>
            {isPresetsDropdownOpen &&
                presetsDropdownPosition &&
                resolvedPortalTarget &&
                createPortal(
                    <div
                        ref={presetsDropdownRef}
                        data-placement={presetsDropdownPosition.placement}
                        data-click-outside-ignore="true"
                        className="pointer-events-auto fixed rounded-md border border-slate-200 bg-white py-0.5 shadow-lg"
                        onMouseDown={(event) => {
                            event.stopPropagation();
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                        style={{
                            top: presetsDropdownPosition.top,
                            left: presetsDropdownPosition.left,
                            width: presetsDropdownPosition.width,
                            zIndex: dropdownLayer,
                        }}
                    >
                        {normalizedPresetValues.map((value) => (
                            <button
                                key={value}
                                type="button"
                                className="w-full px-2 py-1 text-center text-xs text-slate-900 transition-colors hover:bg-gray-50"
                                onClick={() => onPresetValueClick(value)}
                            >
                                {value}
                            </button>
                        ))}
                    </div>,
                    resolvedPortalTarget,
                )}
        </div>
    );
};

export default Pagination;
